import { formatUnitAmountCents } from '@helpers/tools';
import { PRODUCT_TYPE } from '@models/product';
import { VARIANT_PERIOD_UNIT, VARIANT_TYPE } from '@models/variant';
import { useCheckoutStore } from '@stores/checkout/checkoutStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

export const useWeightLossPlans = () => {
  const checkoutStore = useCheckoutStore();

  const { variantForm, mainProduct } = storeToRefs(checkoutStore);

  // TODO: Change to i18n pluralization
  const pluralize = (value: number, unit: string) => {
    return value === 1 ? `${value} ${unit}` : `${value} ${unit}s`;
  };

  const monthPeriodPrice = computed(() => {
    return mainProduct.value?.product.variants.find((variant) => {
      return variant.variantType === VARIANT_TYPE.SUBSCRIPTION
        && variant.displayInQuiz
        && variant.variantPeriod
        && variant.variantPeriod === 1
        && variant.variantPeriodUnit === VARIANT_PERIOD_UNIT.MONTH;
    })?.variantAmount || 0;
  });

  // Create sorted options to display in the template
  const periodOptions = computed(() => {
    const monthsPerUnit = { day: 0.033, week: 0.25, month: 1, year: 12 };
    const isInjection = mainProduct.value?.product.productType.includes(PRODUCT_TYPE.INJECTION);

    return mainProduct.value?.product.variants
      .filter((variant) => (
        variant.variantType === VARIANT_TYPE.SUBSCRIPTION
          && variant.displayInQuiz
          && variant.variantPeriod
      ))
      .map((variant) => {
        const monthCount = monthsPerUnit[variant.variantPeriodUnit!] * variant.variantPeriod!;
        const pricePerMonth = variant.variantAmount / monthCount;
        const periodText = pluralize(variant.variantPeriod!, variant.variantPeriodUnit!);

        const monthPriceText = monthPeriodPrice.value - pricePerMonth > 0
          ? `${formatUnitAmountCents(monthPeriodPrice.value)}/mo`
          : '';

        const offPriceText = monthPeriodPrice.value - pricePerMonth > 0
          ? `${formatUnitAmountCents(monthPeriodPrice.value - pricePerMonth)} off!`
          : '';

        return {
          id: `${variant.variantPeriod}${variant.variantPeriodUnit}`,
          title: `${monthCount} Month Plan`,
          label: variant.variantLabel ?? '',
          agreements: variant.variantPaymentAgreements ?? '',
          formattedPrice: `${formatUnitAmountCents(pricePerMonth)}/mo`,
          monthPrice: monthPriceText,
          offPrice: offPriceText,
          recommended: !!variant.variantRecommended,
          list: [
            `${formatUnitAmountCents(variant.variantAmount)} charged every ${periodText}`,
            `Equivalent to paying ${formatUnitAmountCents(pricePerMonth)}/mo`,
            `Includes ${periodText} of medication + ${isInjection ? 'injection supplies' : 'supplies'}`,
            `Ships every ${(variant.variantPeriodUnit === VARIANT_PERIOD_UNIT.MONTH
            && variant.variantPeriod! > 3)
            || variant.variantPeriodUnit === VARIANT_PERIOD_UNIT.YEAR
              ? pluralize(3, VARIANT_PERIOD_UNIT.MONTH)
              : pluralize(variant.variantPeriod!, variant.variantPeriodUnit!)}`,
          ],
          monthCount,
          value: variant.variantPeriod,
          unit: variant.variantPeriodUnit,
          paymentDescription: `If prescribed, you’ll be charged ${formatUnitAmountCents(variant.variantAmount)} every ${periodText}. You can cancel anytime prior to your next billing cycle.`,
        };
      })
      .sort((a, b) => b.monthCount - a.monthCount) || [];
  });

  const activePeriodOption = computed(() => {
    return periodOptions.value.find((option) => {
      return option.value === variantForm.value.period.value
          && option.unit === variantForm.value.period.unit;
    });
  });

  return {
    periodOptions,
    activePeriodOption,
  };
};
